<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useNamespace } from '@zoryn/hooks'

interface ShareMessage {
  id: string
  role: string
  content: string
}

interface ShareSnapshot {
  token: string
  sessionId: string | null
  title: string
  messages: ShareMessage[]
  createdAt: string
}

defineOptions({ name: 'ZAiShare' })

const ns = useNamespace('ai-share')
const route = useRoute()
const router = useRouter()
const snapshot = ref<ShareSnapshot | null>(null)
const missing = ref(false)

const token = computed(() => {
  const raw = route.params.token
  return typeof raw === 'string' ? raw : ''
})

function loadSnapshot(): void {
  const key = 'seeker.share.snapshots'
  try {
    const raw = localStorage.getItem(key)
    const map = raw ? (JSON.parse(raw) as Record<string, ShareSnapshot>) : {}
    const hit = token.value ? map[token.value] : null
    if (hit?.messages?.length) {
      snapshot.value = hit
      missing.value = false
    } else {
      snapshot.value = null
      missing.value = true
    }
  } catch {
    snapshot.value = null
    missing.value = true
  }
}

function goChat(): void {
  void router.push('/ai/chat')
}

onMounted(loadSnapshot)
</script>

<template>
  <div :class="ns.b()">
    <header :class="ns.e('head')">
      <h1>{{ snapshot?.title || '分享的对话' }}</h1>
      <button type="button" :class="ns.e('back')" @click="goChat">返回助手</button>
    </header>

    <p v-if="missing" :class="ns.e('empty')">
      未找到该分享内容。链接可能已过期，或仅在创建它的本机浏览器中有效。
    </p>

    <div v-else :class="ns.e('list')">
      <article
        v-for="msg in snapshot?.messages || []"
        :key="msg.id"
        :class="[ns.e('msg'), ns.is('user', msg.role === 'user')]"
      >
        <div :class="ns.e('role')">{{ msg.role === 'user' ? '用户' : '助手' }}</div>
        <pre :class="ns.e('body')">{{ msg.content }}</pre>
      </article>
    </div>
  </div>
</template>
